import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SubjectContext = createContext(null);

const KEY = (uid) => `fp_active_subject_${uid}`;

/** Applied before anything fetches, so no screen can quietly read the wrong person's data. */
const applyHeader = (subjectId) => {
  if (subjectId) axios.defaults.headers.common['X-Subject-Id'] = subjectId;
  else delete axios.defaults.headers.common['X-Subject-Id'];
};

export const useActiveChild = () => {
  const ctx = useContext(SubjectContext);
  if (!ctx) throw new Error('useActiveChild must be used within ActiveChildProvider');
  return ctx;
};

export const useSubject = useActiveChild;

/**
 * Whose FuturePath is being worked on. One selection, applied to every request as X-Subject-Id
 * and re-verified by the backend, so a parent working on a child cannot end up writing to
 * their own profile — the bug this replaces.
 */
export const ActiveChildProvider = ({ children }) => {
  const { user, getAuthHeader, isAuthenticated } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState(() => {
    // Read and apply synchronously: the first fetch of any page must already be scoped.
    // Strictly the signed-in user's own selection — reading whichever key happened to be in
    // localStorage let a new account on a shared browser inherit the last person's subject and
    // hit a read-only wall instead of their own onboarding.
    try {
      const uid = localStorage.getItem('futurepath_uid');
      Object.keys(localStorage)
        .filter((k) => k.startsWith('fp_active_subject_') && (!uid || k !== KEY(uid)))
        .forEach((k) => localStorage.removeItem(k));
      const stored = uid ? localStorage.getItem(KEY(uid)) : null;
      applyHeader(stored);
      return stored;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isAuthenticated || !user?.id) { setSubjects([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/subjects`, { ...getAuthHeader(), withCredentials: true });
      const rows = r.data?.subjects || [];
      setSubjects(rows);
      // A stored subject that isn't one of this person's must not linger — it would scope every
      // request to somebody they can't write to.
      if (subjectId && !rows.some((s) => s.id === subjectId)) {
        localStorage.removeItem(KEY(user.id));
        applyHeader(null);
        setSubjectId(null);
      }
    } catch {
      setSubjects([]);
      // If we can't confirm who they're allowed to act for, act as themselves.
      if (subjectId) {
        localStorage.removeItem(KEY(user.id));
        applyHeader(null);
        setSubjectId(null);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, getAuthHeader, subjectId]);

  useEffect(() => { load(); }, [isAuthenticated, user?.id]);   // eslint-disable-line

  /** Switching reloads the app: every module refetches for the new person, nothing stays behind. */
  const switchSubject = useCallback((id, destination = '/app/dashboard') => {
    if (!user?.id) return;
    const self = !id || id === user.id;
    if (self) localStorage.removeItem(KEY(user.id));
    else localStorage.setItem(KEY(user.id), id);
    applyHeader(self ? null : id);
    window.location.assign(destination);
  }, [user?.id]);

  const activeSubject = useMemo(
    () => subjects.find((s) => s.id === subjectId) || null, [subjects, subjectId]);

  const value = {
    subjects,
    activeSubject,
    subjectId: subjectId || user?.id || null,
    isSelf: !subjectId || subjectId === user?.id,
    mode: activeSubject?.mode || 'self',
    // work_as (under 13) and setup_for (a 13–17 profile the parent created and the teen hasn't
    // claimed) may be written to. Once the young person owns their password it's read-only.
    canWrite: !subjectId || subjectId === user?.id
      || ['work_as', 'setup_for'].includes(activeSubject?.mode),
    switchSubject,
    loading,
    refresh: load,
    // kept for the screens that already consumed the older child API
    children: subjects.filter((s) => !s.is_self),
    activeChild: activeSubject && !activeSubject.is_self ? activeSubject : null,
    activeChildId: subjectId && subjectId !== user?.id ? subjectId : null,
    setActiveChildId: switchSubject,
    isParent: user?.role === 'parent',
  };

  return <SubjectContext.Provider value={value}>{children}</SubjectContext.Provider>;
};

export default SubjectContext;
